import React, { useState, useEffect } from "react";
import axios from "axios";
import classes from "./RolesPermissions.module.css";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiRefreshCw,
  FiChevronRight,
  FiChevronDown,
  FiCheck,
  FiX,
  FiUser,
  FiUsers,
  FiLock,
  FiShield,
  FiGrid,
} from "react-icons/fi";
import { BASE_URL } from "../../Components/API/Api";

const RolesPermissions = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  // State
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);

  // Sample permissions data
  const modules = [
    { id: 1, name: "Dashboard", icon: <FiGrid /> },
    { id: 2, name: "Patients", icon: <FiUsers /> },
    { id: 3, name: "Appointments", icon: <FiGrid /> },
    { id: 4, name: "Doctors", icon: <FiUser /> },
    { id: 5, name: "Staff", icon: <FiUsers /> },
    { id: 6, name: "Laboratory", icon: <FiGrid /> },
    { id: 7, name: "Pharmacy", icon: <FiGrid /> },
    { id: 8, name: "Billing & Payments", icon: <FiGrid /> },
    { id: 9, name: "Admissions", icon: <FiGrid /> },
    { id: 10, name: "Reports", icon: <FiGrid /> },
    { id: 11, name: "Settings", icon: <FiGrid /> },
    { id: 12, name: "Users & Roles", icon: <FiGrid /> },
  ];

  const permissions = ["View", "Create", "Delete", "Export"];

  // Sample roles data
  const sampleRoles = [
    {
      id: 1,
      name: "Super Administrator",
      type: "System Role",
      description: "Has full access to all modules and features.",
      users: 1,
      status: "Active",
      permissions: {
        dashboard: { view: true, create: true, delete: true, export: true },
        patients: { view: true, create: true, delete: true, export: true },
        appointments: { view: true, create: true, delete: true, export: true },
        doctors: { view: true, create: true, delete: true, export: true },
        staff: { view: true, create: true, delete: true, export: true },
        laboratory: { view: true, create: true, delete: true, export: true },
        pharmacy: { view: true, create: true, delete: true, export: true },
        billing: { view: true, create: true, delete: true, export: true },
        admissions: { view: true, create: true, delete: true, export: true },
        reports: { view: true, create: true, delete: true, export: true },
        settings: { view: true, create: true, delete: true, export: true },
        users_roles: { view: true, create: true, delete: true, export: true },
      }
    },
    {
      id: 2,
      name: "Administrator",
      type: "Custom Role",
      description: "Full access except system settings and user management.",
      users: 3,
      status: "Active",
      permissions: {
        dashboard: { view: true, create: true, delete: true, export: true },
        patients: { view: true, create: true, delete: true, export: true },
        appointments: { view: true, create: true, delete: true, export: true },
        doctors: { view: true, create: true, delete: true, export: true },
        staff: { view: true, create: true, delete: true, export: true },
        laboratory: { view: true, create: true, delete: true, export: true },
        pharmacy: { view: true, create: true, delete: true, export: true },
        billing: { view: true, create: true, delete: true, export: true },
        admissions: { view: true, create: true, delete: true, export: true },
        reports: { view: true, create: true, delete: true, export: true },
        settings: { view: false, create: false, delete: false, export: false },
        users_roles: { view: true, create: false, delete: false, export: false },
      }
    },
    {
      id: 3,
      name: "Doctor",
      type: "Custom Role",
      description: "Access to patient records, appointments, and prescriptions.",
      users: 12,
      status: "Active",
      permissions: {
        dashboard: { view: true, create: false, delete: false, export: false },
        patients: { view: true, create: true, delete: false, export: false },
        appointments: { view: true, create: true, delete: false, export: false },
        doctors: { view: true, create: false, delete: false, export: false },
        staff: { view: false, create: false, delete: false, export: false },
        laboratory: { view: true, create: true, delete: false, export: false },
        pharmacy: { view: true, create: true, delete: false, export: false },
        billing: { view: false, create: false, delete: false, export: false },
        admissions: { view: true, create: true, delete: false, export: false },
        reports: { view: true, create: false, delete: false, export: false },
        settings: { view: false, create: false, delete: false, export: false },
        users_roles: { view: false, create: false, delete: false, export: false },
      }
    },
    {
      id: 4,
      name: "Nurse",
      type: "Custom Role",
      description: "Access to patient care, vitals, and medication administration.",
      users: 18,
      status: "Active",
      permissions: {
        dashboard: { view: true, create: false, delete: false, export: false },
        patients: { view: true, create: true, delete: false, export: false },
        appointments: { view: true, create: false, delete: false, export: false },
        doctors: { view: true, create: false, delete: false, export: false },
        staff: { view: false, create: false, delete: false, export: false },
        laboratory: { view: true, create: true, delete: false, export: false },
        pharmacy: { view: true, create: true, delete: false, export: false },
        billing: { view: false, create: false, delete: false, export: false },
        admissions: { view: true, create: false, delete: false, export: false },
        reports: { view: false, create: false, delete: false, export: false },
        settings: { view: false, create: false, delete: false, export: false },
        users_roles: { view: false, create: false, delete: false, export: false },
      }
    },
    {
      id: 5,
      name: "Laboratory Technician",
      type: "Custom Role",
      description: "Access to lab tests, results, and diagnostic tools.",
      users: 6,
      status: "Active",
      permissions: {
        dashboard: { view: true, create: false, delete: false, export: false },
        patients: { view: true, create: false, delete: false, export: false },
        appointments: { view: false, create: false, delete: false, export: false },
        doctors: { view: false, create: false, delete: false, export: false },
        staff: { view: false, create: false, delete: false, export: false },
        laboratory: { view: true, create: true, delete: false, export: false },
        pharmacy: { view: false, create: false, delete: false, export: false },
        billing: { view: false, create: false, delete: false, export: false },
        admissions: { view: false, create: false, delete: false, export: false },
        reports: { view: true, create: false, delete: false, export: false },
        settings: { view: false, create: false, delete: false, export: false },
        users_roles: { view: false, create: false, delete: false, export: false },
      }
    },
    {
      id: 6,
      name: "Pharmacist",
      type: "Custom Role",
      description: "Access to pharmacy inventory, prescriptions, and dispensing.",
      users: 5,
      status: "Active",
      permissions: {
        dashboard: { view: true, create: false, delete: false, export: false },
        patients: { view: true, create: false, delete: false, export: false },
        appointments: { view: false, create: false, delete: false, export: false },
        doctors: { view: false, create: false, delete: false, export: false },
        staff: { view: false, create: false, delete: false, export: false },
        laboratory: { view: false, create: false, delete: false, export: false },
        pharmacy: { view: true, create: true, delete: false, export: false },
        billing: { view: true, create: false, delete: false, export: false },
        admissions: { view: false, create: false, delete: false, export: false },
        reports: { view: true, create: false, delete: false, export: false },
        settings: { view: false, create: false, delete: false, export: false },
        users_roles: { view: false, create: false, delete: false, export: false },
      }
    },
    {
      id: 7,
      name: "Receptionist",
      type: "Custom Role",
      description: "Access to patient registration, appointments, and front desk.",
      users: 4,
      status: "Active",
      permissions: {
        dashboard: { view: true, create: false, delete: false, export: false },
        patients: { view: true, create: true, delete: false, export: false },
        appointments: { view: true, create: true, delete: false, export: false },
        doctors: { view: true, create: false, delete: false, export: false },
        staff: { view: false, create: false, delete: false, export: false },
        laboratory: { view: false, create: false, delete: false, export: false },
        pharmacy: { view: false, create: false, delete: false, export: false },
        billing: { view: false, create: false, delete: false, export: false },
        admissions: { view: true, create: true, delete: false, export: false },
        reports: { view: false, create: false, delete: false, export: false },
        settings: { view: false, create: false, delete: false, export: false },
        users_roles: { view: false, create: false, delete: false, export: false },
      }
    },
    {
      id: 8,
      name: "Billing Officer",
      type: "Custom Role",
      description: "Access to billing, invoicing, and payment processing.",
      users: 4,
      status: "Active",
      permissions: {
        dashboard: { view: true, create: false, delete: false, export: false },
        patients: { view: true, create: false, delete: false, export: false },
        appointments: { view: false, create: false, delete: false, export: false },
        doctors: { view: false, create: false, delete: false, export: false },
        staff: { view: false, create: false, delete: false, export: false },
        laboratory: { view: false, create: false, delete: false, export: false },
        pharmacy: { view: false, create: false, delete: false, export: false },
        billing: { view: true, create: true, delete: false, export: false },
        admissions: { view: false, create: false, delete: false, export: false },
        reports: { view: true, create: false, delete: false, export: false },
        settings: { view: false, create: false, delete: false, export: false },
        users_roles: { view: false, create: false, delete: false, export: false },
      }
    },
    {
      id: 9,
      name: "Accountant",
      type: "Custom Role",
      description: "Access to financial reports, accounting, and auditing.",
      users: 2,
      status: "Active",
      permissions: {
        dashboard: { view: true, create: false, delete: false, export: false },
        patients: { view: false, create: false, delete: false, export: false },
        appointments: { view: false, create: false, delete: false, export: false },
        doctors: { view: false, create: false, delete: false, export: false },
        staff: { view: false, create: false, delete: false, export: false },
        laboratory: { view: false, create: false, delete: false, export: false },
        pharmacy: { view: false, create: false, delete: false, export: false },
        billing: { view: true, create: false, delete: false, export: true },
        admissions: { view: false, create: false, delete: false, export: false },
        reports: { view: true, create: false, delete: false, export: true },
        settings: { view: false, create: false, delete: false, export: false },
        users_roles: { view: false, create: false, delete: false, export: false },
      }
    },
  ];

  useEffect(() => {
    // Load sample data (in production, fetch from API)
    setRoles(sampleRoles);
    if (sampleRoles.length > 0) {
      setSelectedRole(sampleRoles[0]);
    }
  }, []);

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const togglePermission = (roleId, moduleKey, permission) => {
    setRoles(prev => prev.map(role => {
      if (role.id === roleId) {
        return {
          ...role,
          permissions: {
            ...role.permissions,
            [moduleKey]: {
              ...role.permissions[moduleKey],
              [permission]: !role.permissions[moduleKey][permission]
            }
          }
        };
      }
      return role;
    }));
  };

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getModuleKey = (moduleName) => {
    const map = {
      "Dashboard": "dashboard",
      "Patients": "patients",
      "Appointments": "appointments",
      "Doctors": "doctors",
      "Staff": "staff",
      "Laboratory": "laboratory",
      "Pharmacy": "pharmacy",
      "Billing & Payments": "billing",
      "Admissions": "admissions",
      "Reports": "reports",
      "Settings": "settings",
      "Users & Roles": "users_roles",
    };
    return map[moduleName] || moduleName.toLowerCase().replace(/ /g, '_');
  };

  return (
    <div className={classes.pageContainer}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={classes.mainArea}>
        <Header toggleSidebar={toggleSidebar} />
        <div className={classes.content}>
          
          {/* Breadcrumb */}
          <div className={classes.breadcrumb}>
            <span>Dashboard</span>
            <FiChevronRight className={classes.breadcrumbIcon} />
            <span>Settings</span>
            <FiChevronRight className={classes.breadcrumbIcon} />
            <span className={classes.breadcrumbActive}>Roles & Permissions</span>
          </div>

          {/* Header */}
          <div className={classes.header}>
            <div>
              <h1 className={classes.pageTitle}>Roles & Permissions</h1>
              <p className={classes.pageSubtitle}>Manage user roles and their permissions.</p>
            </div>
            <button className={classes.addBtn} onClick={() => setShowAddRoleModal(true)}>
              <FiPlus /> Add Role
            </button>
          </div>

          {/* Main Content */}
          <div className={classes.mainContent}>
            {/* Left Panel - Roles List */}
            <div className={classes.leftPanel}>
              <div className={classes.searchWrapper}>
                <FiSearch className={classes.searchIcon} />
                <input
                  type="text"
                  placeholder="Search roles..."
                  className={classes.searchInput}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className={classes.rolesList}>
                {filteredRoles.map((role) => (
                  <div
                    key={role.id}
                    className={`${classes.roleItem} ${selectedRole?.id === role.id ? classes.roleItemActive : ""}`}
                    onClick={() => setSelectedRole(role)}
                  >
                    <div className={classes.roleInfo}>
                      <div className={classes.roleIcon}>
                        <FiShield />
                      </div>
                      <div>
                        <h4>{role.name}</h4>
                        <span>{role.type}</span>
                      </div>
                    </div>
                    <div className={classes.roleBadge}>
                      <span className={classes.userCount}>{role.users} users</span>
                      <span className={`${classes.statusBadge} ${role.status === "Active" ? classes.statusActive : classes.statusInactive}`}>
                        {role.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className={classes.rolesFooter}>
                Showing 1 to {filteredRoles.length} of {roles.length} roles
              </div>
            </div>

            {/* Right Panel - Permissions */}
            <div className={classes.rightPanel}>
              {selectedRole ? (
                <>
                  <div className={classes.roleDetails}>
                    <div className={classes.roleDetailsHeader}>
                      <div>
                        <h2>{selectedRole.name}</h2>
                        <p>{selectedRole.description}</p>
                      </div>
                      <div className={classes.roleDetailsActions}>
                        <button className={classes.editRoleBtn}>
                          <FiEdit2 /> Edit Role
                        </button>
                      </div>
                    </div>
                    <div className={classes.roleDetailsMeta}>
                      <span><FiUser /> {selectedRole.users} users</span>
                      <span className={`${classes.statusBadge} ${selectedRole.status === "Active" ? classes.statusActive : classes.statusInactive}`}>
                        {selectedRole.status}
                      </span>
                      <span className={classes.roleType}>{selectedRole.type}</span>
                    </div>
                  </div>

                  <div className={classes.permissionsSection}>
                    <div className={classes.permissionsHeader}>
                      <h3>Module Permissions</h3>
                      <label className={classes.selectAll}>
                        <input type="checkbox" />
                        Select All
                      </label>
                    </div>

                    <div className={classes.permissionsList}>
                      {modules.map((module) => {
                        const moduleKey = getModuleKey(module.name);
                        const perms = selectedRole.permissions[moduleKey] || { view: false, create: false, delete: false, export: false };
                        const isExpanded = expandedModules[module.id] || false;

                        return (
                          <div key={module.id} className={classes.moduleItem}>
                            <div
                              className={classes.moduleHeader}
                              onClick={() => toggleModule(module.id)}
                            >
                              <div className={classes.moduleTitle}>
                                <span className={classes.moduleIcon}>{module.icon}</span>
                                <span>{module.name}</span>
                              </div>
                              <div className={classes.moduleActions}>
                                <div className={classes.permissionChips}>
                                  {permissions.map((perm) => (
                                    <span
                                      key={perm}
                                      className={`${classes.permissionChip} ${perms[perm.toLowerCase()] ? classes.permissionChipActive : ""}`}
                                    >
                                      {perms[perm.toLowerCase()] ? <FiCheck /> : <FiX />}
                                      {perm}
                                    </span>
                                  ))}
                                </div>
                                <span className={classes.moduleChevron}>
                                  {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
                                </span>
                              </div>
                            </div>

                            {isExpanded && (
                              <div className={classes.modulePermissions}>
                                {permissions.map((perm) => (
                                  <label key={perm} className={classes.permissionCheckbox}>
                                    <input
                                      type="checkbox"
                                      checked={perms[perm.toLowerCase()] || false}
                                      onChange={() => togglePermission(selectedRole.id, moduleKey, perm.toLowerCase())}
                                    />
                                    {perm}
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className={classes.roleFooter}>
                    <p>{selectedRole.name} role has full access to all modules and features in the system.</p>
                  </div>
                </>
              ) : (
                <div className={classes.noRoleSelected}>
                  <FiShield className={classes.noRoleIcon} />
                  <p>Select a role to view and manage permissions</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolesPermissions;